import { CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useCallback, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Input from "ds/components/Input";
import Tile from "ds/components/Tile";
import TileTitle from "ds/components/Tile/Title";
import TileWrapper from "ds/components/Tile/Wrapper";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageBilling } from "hooks/useAnalytics/pages/billing";
import useTypedContext from "hooks/useTypedContext";
import { BillingCycleInterval } from "types/generated";
import { stringIsRequired, validateEmail } from "utils/formValidators";
import Card from "ds/components/Card";

import BillingAddressFormFields from "../../AddressFormFields";
import BillingCardFields from "../../CardFormFields";
import BillingAddonFeatureToggles from "../../components/AddonFeatureToggles";
import PaymentsPartner from "../../components/PaymentsPartner";
import BillingTwoColumnLayout from "../../components/TwoColumnLayout";
import BillingTwoColumnLayoutLeft from "../../components/TwoColumnLayout/Left";
import BillingTwoColumnLayoutRight from "../../components/TwoColumnLayout/Right";
import { STARTER_ANNUAL_DISCOUNT } from "../../constants";
import useStarterPlanInfo from "../../hooks/useStarterPlanInfo";
import BillSummary from "../BillSummary";
import { BillingUpgradeContext } from "../context";
import PoweredByStripeBanner from "../PoweredByStripeBanner";
import { UpgradePlanFields } from "../types";

const BillingUpgradeDetails = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { goToPreviousStep, goToNextStep, setPaymentMethod } =
    useTypedContext(BillingUpgradeContext);
  const { onError, reportError } = useTypedContext(FlashContext);

  const [isCardFieldsValid, setCardFieldsValid] = useState(false);

  const {
    control,
    register,
    formState: { errors, isValid, isSubmitting },
    watch,
    handleSubmit,
    setValue,
  } = useFormContext<UpgradePlanFields>();

  const interval = watch("interval");

  const addons = watch("addons");

  const trackSegmentEvent = useAnalytics({
    page: AnalyticsPageBilling.BillingUpgrade,
  });

  const submit = useCallback(
    () =>
      handleSubmit(async () => {
        if (!stripe || !elements) {
          // Stripe.js has not loaded yet. Make sure to disable
          // form submission until Stripe.js has loaded.
          return;
        }

        // Get a reference to a mounted CardElement. Elements knows how
        // to find your CardElement because there can only ever be one of
        // each type of element.
        const cardElement = elements.getElement(CardNumberElement);

        if (cardElement) {
          // Use your card Element with other Stripe.js APIs
          const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
          });

          if (!error) {
            setPaymentMethod(paymentMethod);
            goToNextStep();
            trackSegmentEvent("Payment Method Created", {
              interval,
              addons,
            });

            return;
          }

          if (error.type === "validation_error") {
            // Nothing to do, we've already displayed the error in the card element
            return;
          }

          if (error.message) {
            reportError({ message: error.message });
          } else {
            onError(error);
          }
        } else {
          reportError({ message: "Unable to read card details" });
        }
      })(),
    [
      stripe,
      elements,
      setPaymentMethod,
      goToNextStep,
      trackSegmentEvent,
      interval,
      addons,
      reportError,
      onError,
      handleSubmit,
    ]
  );

  const { monthlyPrice, annualPrice } = useStarterPlanInfo(interval, addons);

  return (
    <>
      <BillingTwoColumnLayout>
        <BillingTwoColumnLayoutLeft>
          <Box direction="column" gap="x-large" fullWidth>
            <TileWrapper gap="x-large" direction="column" grow="0">
              <Typography tag="h3" variant="p-t5">
                Starter plan
              </Typography>
              <Controller
                name="interval"
                control={control}
                render={({ field }) => (
                  <Box gap="x-large">
                    <Tile
                      title="Pay monthly"
                      indicator="radio"
                      selected={field.value === BillingCycleInterval.Monthly}
                      onClick={() => field.onChange(BillingCycleInterval.Monthly)}
                    >
                      <Typography variant="p-t2" tag="span">
                        $ {monthlyPrice}
                      </Typography>
                    </Tile>
                    <Tile
                      title={
                        <Box gap="medium">
                          <TileTitle>Pay annually</TileTitle>
                          <Badge
                            type="weak"
                            variant="green"
                            text={`Save ${STARTER_ANNUAL_DISCOUNT}%`}
                          />
                        </Box>
                      }
                      indicator="radio"
                      selected={field.value === BillingCycleInterval.Yearly}
                      onClick={() => field.onChange(BillingCycleInterval.Yearly)}
                    >
                      <Typography variant="p-t2" tag="span">
                        $ {annualPrice}
                      </Typography>
                    </Tile>
                  </Box>
                )}
              />

              <BillingAddonFeatureToggles
                interval={interval}
                addons={addons}
                onChange={(addons) => setValue("addons", addons)}
              />
            </TileWrapper>

            <Card
              variant="secondary"
              titleTag="h3"
              title="Your payment details"
              contentGap="x-large"
            >
              <FormField label="Full name" error={errors?.fullName?.message} noMargin>
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="e.g. John Smith"
                    error={!!errors?.fullName}
                    {...register("fullName", {
                      validate: stringIsRequired("Full name is required."),
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>
              <FormField
                label="Email"
                error={errors?.email?.message}
                tooltipInfoVariant="modal"
                noMargin
              >
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Email"
                    error={!!errors?.email}
                    {...register("email", {
                      required: "Email is required.",
                      validate: validateEmail,
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>
              <BillingCardFields onValidationUpdate={setCardFieldsValid} />
            </Card>
            <Card title="Billing information" titleTag="h3" contentGap="x-large">
              <BillingAddressFormFields />
            </Card>
          </Box>
        </BillingTwoColumnLayoutLeft>
        <BillingTwoColumnLayoutRight>
          <Box direction="column" gap="x-large" fullWidth>
            <BillSummary />
            <PaymentsPartner withTile />
          </Box>
        </BillingTwoColumnLayoutRight>
      </BillingTwoColumnLayout>
      <FullScreenModalFooter justify="between">
        <PoweredByStripeBanner />
        <Box gap="medium" justify="end">
          <Button variant="secondary" onPress={goToPreviousStep}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={submit}
            loading={isSubmitting}
            disabled={!isValid || !isCardFieldsValid || isSubmitting}
            analyticsPage={AnalyticsPageBilling.BillingUpgrade}
            analyticsTitle="Payment Summary Clicked"
            analyticsProps={{
              interval,
              addons,
            }}
          >
            Payment summary
          </Button>
        </Box>
      </FullScreenModalFooter>
    </>
  );
};

export default BillingUpgradeDetails;
